import React from "react";
import { CheckIcon, ErrorIcon, SubtractMinimizeIcon } from "@directv-digital/fusion.atoms.icons";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";

// --- Interfaces ---
export interface CompetitorLogoStyle {
  width?: string | { sm?: string; xs?: string; };
  height?: string;
  display?: string;
  objectFit?: string;
}

export interface CompetitorDetails {
  items?: Record<string, string>;
}

export interface Competitor {
  logo?: string;
  label?: string;
  details?: CompetitorDetails;
  logoStyle?: CompetitorLogoStyle;
}

export interface MainPackage {
  items?: Record<string, string>;
  label?: string;
}

export interface ColumnWidths {
  dtvColWidth?: Record<string, string>;
  featureColWidth?: Record<string, string>;
  competitorColWidth?: Record<string, string>;
}

export interface AriaProps {
  no?: string;
  yes?: string;
  footerNote?: string;
  tableLabel?: string;
  dtvColLabel?: string;
  featureLabel?: string;
  notAvailable?: string;
  selectCompetitor?: string;
  selectDtvPackage?: string;
  competitorColLabel?: string;
  competitorSelector?: string;
  dtvPackageSelector?: string;
  competitorSelectorDesc?: string;
  dtvPackageSelectorDesc?: string;
}

export interface TooltipProps {
  content?: string;
  enabled?: boolean;
}

export interface FeaturesProps {
  items?: Record<string, string>;
}

export interface LegalTextProps {
  items?: string[];
}

export interface DirectvLogoProps {
  url?: string;
}

export interface CompetitorTableProps {
  aria?: AriaProps;
  tooltip?: TooltipProps;
  featureOrder?: string[];
  features?: FeaturesProps;
  legaltext?: LegalTextProps;
  directvLogo?: DirectvLogoProps;
  columnWidths?: ColumnWidths;
  mainPackages?: Array<Record<string, MainPackage>>;
  competitorsList?: Array<Record<string, Competitor>>;
  title?: string;
}

export type DtvCompetitorComparisonTableProps = {
  competitorTableProps?: CompetitorTableProps;
};

// --- Utility functions ---
export const formatAria = (template: string | undefined, values: Record<string, string | number>) => template?.replace(/\{(\w+)\}/g, (_, k) => (values?.[k] !== undefined ? String(values[k]) : "")) || "";

export const isIcon = (val: any) => val === true || val === false || val === "NA" || val === "true" || val === "false";

export const renderValue = (
  value: any,
  feature: string,
  colLabel: string,
  ariaTemplates: Partial<AriaProps> | undefined,
  isGenrePack: boolean
) => {
  let ariaLabel = "";
  let normalizedValue = value;
  if (value === true || value === "true") {
    ariaLabel = ariaTemplates?.yes || "";
    normalizedValue = true;
  } else if (value === false || value === "false") {
    ariaLabel = ariaTemplates?.no || "";
    normalizedValue = false;
  } else if (value === "NA") {
    ariaLabel = ariaTemplates?.notAvailable || "";
  } else {
    ariaLabel = formatAria(ariaTemplates?.featureLabel, {
      col: colLabel || "",
      feature: feature || "",
      value: value ?? ""
    });
  }

  if (normalizedValue === true) {
    return (
      <span aria-label={ariaLabel} role="img">
        <CheckIcon desc={ariaLabel} fill="#1E815D" title={ariaLabel} />
      </span>
    );
  }
  if (normalizedValue === false) {
    return (
      <span aria-label={ariaLabel} role="img">
        <ErrorIcon desc={ariaLabel} fill="#E35A4D" title={ariaLabel} />
      </span>
    );
  }
  if (value === "NA") {
    return (
      <span aria-label={ariaLabel} role="img">
        <SubtractMinimizeIcon desc={ariaLabel} fill="#8D8D8D" title={ariaLabel} />
      </span>
    );
  }
  // Generated by Copilot- Start - 2026-03-19 16:42:40
  // Handle structured monthlyCost object from adapter: { strikeoutText, offerText }
  if (value && typeof value === "object" && (value?.strikeoutText || value?.offerText)) {
    const { strikeoutText, offerText } = value;
    const structuredAriaLabel = `${strikeoutText || ""} ${offerText || ""}`.trim();
    return (
      <DtvBox
        aria-label={structuredAriaLabel}
        role="group"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2px"
        }}
      >
        {strikeoutText && (
          <DtvText
            aria-label={`Regular price ${strikeoutText}`}
            component="span"
            content={strikeoutText}
            sx={{
              fontSize: {
 xs: "10px",
sm: "12px"
},
              color: colorPalette.gray[500],
              textDecoration: "line-through"
            }}
          />
        )}
        {offerText && (
          <DtvText
            aria-label={`Offer price ${offerText}`}
            component="span"
            content={offerText}
            sx={{
              fontSize: {
 xs: "12px",
sm: "14px"
},
              fontWeight: 700,
              color: colorPalette.jet.main
            }}
          />
        )}
      </DtvBox>
    );
  }
  // Generated by Copilot- End - 2026-03-19 16:42:40
  return (
    <DtvText
      aria-label={ariaLabel}
      content={value ?? ""}
      variant="body3"
      sx={{
 wordBreak: "normal",
fontSize: isGenrePack ? "14px" : "12px !important",
textAlign: isGenrePack ? "left" : "center"
}}
    />
  );
};
